import { useCallback, useEffect, useState } from "react";
import { getGame } from "../lib/coachGames";

/**
 * Issue #745 — one `GET /api/coach-games/:id`, as a hook. Team selection reads
 * the game once to decide which screen it is (D316 Q3: a published game opens
 * On the day from its date, Pick the XI before it), then hands the same read to
 * the screen it opens, so that screen does not fetch it a second time.
 *
 * `{ status, game, reload }`: `status` is `loading`, `failed` or `ready`
 * (`idle` while `skip` is set); `reload` asks again. Loading, failed and ready
 * are separate states (D53); a failed read is never read as an empty game.
 */
export default function useCoachGame(gameId, { skip = false } = {}) {
  const [state, setState] = useState({ id: gameId, status: skip ? "idle" : "loading", game: null });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (skip) return undefined;
    let live = true;
    setState({ id: gameId, status: "loading", game: null });
    getGame(gameId)
      .then((game) => live && setState({ id: gameId, status: "ready", game }))
      .catch((e) => {
        console.error("useCoachGame: failed to load the game", e);
        if (live) setState({ id: gameId, status: "failed", game: null });
      });
    return () => {
      live = false;
    };
  }, [gameId, reloadKey, skip]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  // For the render between a new id and the effect that starts its read, the old game is not this one.
  if (!skip && state.id !== gameId) return { status: "loading", game: null, reload };
  return { status: state.status, game: state.game, reload };
}
